/**
 * DriveZone — ProximitySearch Panel
 * Left panel of the split-screen layout.
 * Enter one US city → get cities within a 40–45 min drive window.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Info, Loader2, X } from 'lucide-react'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { useSearchStore } from '@/stores/searchStore'
import LocationInput from '@/components/LocationInput'
import ResultCard from '@/components/ResultCard'
import SkeletonCard from '@/components/SkeletonCard'
import type { ProximityResult } from '@/stores/searchStore'
import { AxiosError } from 'axios'

const MODAL_DISMISSED_KEY = 'dz_login_modal_dismissed'

export default function ProximitySearch() {
  const navigate = useNavigate()
  const { user, updateUser } = useAuthStore()

  const [showLoginModal, setShowLoginModal] = useState(false)

  useEffect(() => {
    if (!user && !sessionStorage.getItem(MODAL_DISMISSED_KEY)) {
      const t = setTimeout(() => setShowLoginModal(true), 1200)
      return () => clearTimeout(t)
    }
  }, [user])

  function dismissModal() {
    sessionStorage.setItem(MODAL_DISMISSED_KEY, '1')
    setShowLoginModal(false)
  }
  const {
    proximityResults, setProximityResults,
    proximityLoading, setProximityLoading,
    proximityError,   setProximityError,
    setShowQuotaModal,
  } = useSearchStore()

  const [origin, setOrigin]         = useState('')
  const [minMinutes, setMinMinutes] = useState<number | null>(null)
  const [maxMinutes, setMaxMinutes] = useState<number | null>(null)

  function handleClear() {
    setOrigin('')
    setMinMinutes(null)
    setMaxMinutes(null)
    setProximityResults(null)
    setProximityError('')
  }

  async function handleSearch() {
    if (!origin.trim()) { setProximityError('Please enter a location.'); return }
    if (minMinutes === null || maxMinutes === null) { setProximityError('Please select a drive range first.'); return }
    if (minMinutes >= maxMinutes) { setProximityError('Min must be less than max.'); return }

    setProximityError('')
    setProximityLoading(true)
    setProximityResults(null)

    try {
      const res = await api.post<ProximityResult>('/api/search/proximity/', {
        origin: origin.trim(),
        min_drive_minutes: minMinutes,
        max_drive_minutes: maxMinutes,
      })
      setProximityResults(res.data)

      document.title = 'DriveZone'

      // Update quota counter in auth store so the banner refreshes
      if (user && !user.is_subscribed) {
        updateUser({
          free_searches_used: user.free_searches_used + 1,
          can_search: (user.free_searches_used + 1) < (2 + (user.referral_searches_earned ?? 0)),
        })
      }
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string; message?: string }>
      if (axiosErr.response?.status === 402) {
        setShowQuotaModal(true)
      } else if (axiosErr.response?.status === 503) {
        setProximityError('Google Maps is temporarily unavailable. Please try again in a moment.')
      } else {
        setProximityError(
          axiosErr.response?.data?.message ?? 'Search failed. Please try again.'
        )
      }
    } finally {
      setProximityLoading(false)
    }
  }

  const freeRemaining = user ? Math.max(0, (2 + (user.referral_searches_earned ?? 0)) - user.free_searches_used) : 0
  const showQuotaBanner = user && !user.is_subscribed

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 pb-4 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-900">Find Nearby Cities</h2>
      </div>

      {/* Controls */}
      <div className="flex-shrink-0 space-y-3 pt-4">
        <LocationInput
          placeholder="Origin city, e.g. Trenton, TX"
          onSelect={setOrigin}
          value={origin}
          onChange={setOrigin}
          autoFocus
        />

        {/* Range presets */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Choose range</label>
          <div className="flex gap-2">
            {([
              { label: '20–30 min', min: 20, max: 30 },
              { label: '30–40 min', min: 30, max: 40 },
              { label: '40–50 min', min: 40, max: 50 },
              { label: '50–60 min', min: 50, max: 60 },
            ] as const).map((preset) => {
              const active = minMinutes === preset.min && maxMinutes === preset.max
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => { setMinMinutes(preset.min); setMaxMinutes(preset.max) }}
                  className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-colors
                    ${active
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-primary-400 hover:text-primary-600'
                    }`}
                >
                  {preset.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSearch}
            disabled={proximityLoading || !origin.trim()}
            className="btn-primary flex-1"
          >
            {proximityLoading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</>
              : <><Search className="w-4 h-4" /> Search</>
            }
          </button>
          {(origin.trim() || proximityResults) && !proximityLoading && (
            <button
              onClick={handleClear}
              className="btn-secondary px-3 flex-shrink-0"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Free tier banner */}
        {showQuotaBanner && (
          <div className={`rounded-lg px-3 py-2 text-xs font-medium text-center
            ${freeRemaining === 0
              ? 'bg-red-50 text-red-600 border border-red-200'
              : freeRemaining <= 2
              ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-slate-50 text-slate-500 border border-slate-200'
            }`}
          >
            {freeRemaining === 0
              ? 'No free searches remaining — subscribe to continue'
              : `${freeRemaining} free search${freeRemaining !== 1 ? 'es' : ''} remaining`
            }
          </div>
        )}

        {proximityError && (
          <p className="text-sm text-red-500 text-center">{proximityError}</p>
        )}
      </div>

      {/* Tip */}
      <div className="flex-shrink-0 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mt-3">
        <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-600">
          Try widening your range to find more cities in your state.
        </p>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto min-h-0 pt-3 space-y-2">
        {proximityLoading && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}

        {!proximityLoading && proximityResults && proximityResults.results.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-12 gap-2">
            <Search className="w-8 h-8 opacity-30 text-slate-400" />
            <p className="text-sm font-medium text-slate-500">No cities found</p>
            <p className="text-xs text-slate-400 max-w-xs">
              No cities within {proximityResults.drive_window} of{' '}
              {proximityResults.origin.name.split(',')[0]}.
              Try widening your range.
            </p>
          </div>
        )}

        {!proximityLoading && proximityResults && proximityResults.results.length > 0 && (
          <>
            {proximityResults.note && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200
                              rounded-lg p-3 mb-2">
                <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">{proximityResults.note}</p>
              </div>
            )}

            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-500">
                Results within {proximityResults.drive_window} of{' '}
                <span className="text-slate-700">{proximityResults.origin.name.split(',')[0]}</span>
              </span>
            </div>

            {proximityResults.results.map((result, i) => (
              <ResultCard key={result.name} result={result} rank={i + 1} maxMinutes={maxMinutes ?? undefined} />
            ))}
          </>
        )}

        {!proximityLoading && !proximityResults && !proximityError && (
          <div className="flex flex-col items-center justify-center h-full
                          text-center text-slate-400 py-12 gap-2">
            <Search className="w-8 h-8 opacity-30" />
            <p className="text-sm">Enter a city above to find nearby locations</p>
          </div>
        )}
      </div>

      {/* Login nudge modal — shown once per session for anonymous users */}
      {showLoginModal && !user && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
             style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4 relative">
            <button
              onClick={dismissModal}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-slate-900">Sign in for a better experience</h3>
              <p className="text-sm text-slate-500">
                Track your searches, earn bonus queries by referring friends, and unlock more features.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white
                           text-sm font-semibold transition-colors"
              >
                Sign in
              </button>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 rounded-lg border-2 border-slate-200 hover:border-sky-300
                           text-slate-700 text-sm font-medium transition-colors"
              >
                Create a free account
              </button>
              <button
                onClick={dismissModal}
                className="text-xs text-slate-400 hover:text-slate-600 pt-1 transition-colors"
              >
                Continue without signing in
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
